/**
 * Dubb cookie consent runner.
 *
 * The caller computes a single decision, `autoAccept`, and this drives the
 * vanilla-cookieconsent library accordingly:
 *
 *  - autoAccept = false: the opt-in banner is auto-shown. GTM stays gated until
 *    the visitor accepts.
 *  - autoAccept = true: the `tracking` category is granted immediately and no
 *    banner is shown.
 *
 * Granting `tracking` activates the deferred
 * `<script type="text/plain" data-category="tracking">` GTM tags so analytics
 * and advertising fire without a visible prompt.
 */

/** Minimal surface of the `vanilla-cookieconsent` API we rely on. */
export interface CookieConsentApi {
  run: (config: Record<string, unknown>) => void
  acceptCategory: (categories: string) => void
  show: (createModal?: boolean) => void
}

export interface CookieConsentOptions {
  autoAccept?: boolean
  message?: string
}

export function buildRunConfig(options: CookieConsentOptions): Record<string, unknown> {
  return {
    mode: 'opt-in',
    // Auto-show the opt-in banner unless we are about to grant tracking ourselves.
    autoShow: !options.autoAccept,
    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      tracking: {},
    },
    guiOptions: {
      consentModal: {
        layout: 'bar',
        position: 'bottom',
        equalWeightButtons: true,
      },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Cookie Consent',
            description: options.message,
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject All',
            showPreferencesBtn: 'Manage Preferences',
          },
          preferencesModal: {
            title: 'Cookie Preferences',
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject All',
            savePreferencesBtn: 'Save Preferences',
            sections: [
              {
                title: 'Necessary Cookies',
                description: 'These cookies are essential for the website to function properly and cannot be disabled.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Tracking Cookies',
                description: 'These cookies are used to track visitors and measure website performance through analytics and advertising pixels.',
                linkedCategory: 'tracking',
              },
            ],
          },
        },
      },
    },
  }
}

export function initCookieConsent(
  consent: CookieConsentApi,
  options: CookieConsentOptions = {},
): void {
  consent.run(buildRunConfig(options))

  if (options.autoAccept) {
    // Banner stays hidden (autoShow is false); grant tracking ourselves.
    consent.acceptCategory('all')
  }
}
